import React, { useEffect, useState } from 'react'

import { Autocomplete, Box, Chip, Modal, TextField } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'

import AppLayout from '../../components/Layouts/AppLayout'
import Loading from '../../components/Loading'
import axios from '../../lib/axios'
import { useStateContext } from '../../context/ContextProvider'
import { isValid, handleAxiosError } from '../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../constants/constants'


const STATUS_CONFIG = {
    available: { backgroundColor: '#E3F6EC', color: '#1D8A4C', barColor: '#2FAE60' },
    near_capacity: { backgroundColor: '#FDF1DA', color: '#B5790A', barColor: '#F0A93A' },
    over_capacity: { backgroundColor: '#FBE3E1', color: '#C0362C', barColor: '#E0483C' }
}

const formatHoursMinutes = (value) => {
    if (value === null || value === undefined || value === '') return ''
    const sign = value < 0 ? '-' : ''
    const absValue = Math.abs(value)
    let hours = Math.floor(absValue)
    let minutes = Math.round((absValue - hours) * 60)
    if (minutes === 60) {
        hours += 1
        minutes = 0
    }
    return `${sign}${hours}h ${minutes}m`
}

const modalStyle = {
    minWidth: "360px",
    maxWidth: "900px",
    width: "60%",
    maxHeight: '85vh',
    overflowY: 'auto',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: '#ffffff',
    border: 'transparent',
    borderRadius: '5px',
    boxShadow: 24,
    outline: 'none'
}

const detailsModalStyle = {
    ...modalStyle,
    width: 'fit-content',
    maxWidth: '95vw'
}

const CapacityStatusChip = ({ status, t }) => {
    const style = STATUS_CONFIG[status] ?? STATUS_CONFIG.available
    return (
        <Chip
            label={t(status)}
            size='small'
            sx={{
                backgroundColor: style.backgroundColor,
                borderRadius: '8px',
                height: '24px',
                padding: '4px 12px',
                color: style.color,
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                letterSpacing: '0.15px',
                '& .MuiChip-label': { padding: 0 }
            }}
        />
    )
}

const UtilizationBar = ({ percent, status }) => {
    const style = STATUS_CONFIG[status] ?? STATUS_CONFIG.available
    return (
        <div className='flex items-center gap-2 w-full'>
            <div className='flex-1 rounded-full overflow-hidden' style={{ height: '8px', background: '#EEEFF2' }}>
                <div style={{ width: `${Math.max(0, Math.min(percent, 100))}%`, height: '100%', background: style.barColor }} />
            </div>
            <span style={{ fontSize: '13px', color: '#171717', minWidth: '36px' }}>{percent}%</span>
        </div>
    )
}

const DetailField = ({ label, value, highlight = false }) => (
    <div
        className='flex justify-between items-center'
        style={{ padding: '15px 10px', minHeight: highlight ? '46px' : 'auto', backgroundColor: highlight ? '#F4F8FC' : 'transparent' }}
    >
        <span style={{ fontFamily: 'Roboto, sans-serif', fontWeight: highlight ? 600 : 400, fontSize: '14px', lineHeight: '100%', letterSpacing: '0.15px', color: highlight ? '#336195' : '#88909C' }}>
            {label}
        </span>
        <div style={{ fontFamily: 'Roboto, sans-serif', fontWeight: highlight ? 600 : 400, fontSize: '14px', lineHeight: '100%', letterSpacing: '0.15px', color: highlight ? '#336195' : '#171717', textAlign: 'right', maxWidth: '220px' }}>
            {value}
        </div>
    </div>
)

const CapacityPlanning = () => {
    const { t } = useTranslation()
    const { company_id, choosesite, config, setCompany_id } = useStateContext()

    const [isLoading, setIsLoading] = useState(false)
    const [sites, setSites] = useState([])
    const [capacityResult, setCapacityResult] = useState(null)
    const [lastRunAt, setLastRunAt] = useState(null)

    const [detailsOpen, setDetailsOpen] = useState(false)
    const [selectedRow, setSelectedRow] = useState(null)
    const [pageSize, setPageSize] = useState(10)
    const [workCenterFilter, setWorkCenterFilter] = useState(null)

    /* form */
    const [siteId, setSiteId] = useState('')
    const [siteName, setSiteName] = useState('')
    const [periodFrom, setPeriodFrom] = useState(dayjs())
    const [periodTo, setPeriodTo] = useState(dayjs())

    useEffect(() => {
        if (!isValid(company_id)) {
            setCompany_id(localStorage.getItem('company_id'))
        }
    }, [])

    useEffect(() => {
        if (isValid(company_id)) {
            getSites(company_id)
        }
    }, [company_id])

    useEffect(() => {
        if (!isValid(siteId) && sites.length > 0) {
            const defaultSiteId = choosesite || localStorage.getItem('site')
            const defaultSite = sites.find(site => String(site.id) === String(defaultSiteId))

            if (defaultSite) {
                setSiteId(defaultSite.id)
                setSiteName(defaultSite.name)
            }
        }
    }, [sites, choosesite])

    const getSites = async (id) => {
        await axios
            .get(`/api/sites?company_id=${id}`, config)
            .then(res => {
                setSites(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const handleSiteChange = (a, b) => {
        setSiteId(b?.id || '')
        setSiteName(b?.label || '')
    }

    const isFormValid = () => (
        isValid(siteId)
        && isValid(periodFrom)
        && isValid(periodTo)
    )

    const runCapacityPlanning = async (e) => {
        if (e) e.preventDefault()
        setIsLoading(true)

        const formData = {
            site_id: siteId,
            period_from: dayjs(periodFrom).format('YYYY-MM-DD'),
            period_to: dayjs(periodTo).format('YYYY-MM-DD')
        }

        await axios
            .post('/api/run-basic-capacity-planning', formData, config)
            .then(({ data }) => {
                setCapacityResult(data?.success?.data)
                setWorkCenterFilter(null)
                setLastRunAt(dayjs())
                Swal.fire({
                    icon: "success",
                    customClass: 'success',
                    showCloseButton: true,
                    iconColor: '#00B78E',
                    text: data?.success?.message
                })
            })
            .catch(({ response }) => {
                setCapacityResult(null)
                setLastRunAt(dayjs())
                handleAxiosError({ response: response, showDescription: true })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const openDetails = (row) => {
        setSelectedRow(row)
        setDetailsOpen(true)
    }

    const closeDetails = () => {
        setDetailsOpen(false)
        setSelectedRow(null)
    }

    const optionsSites = sites?.map(option => ({ label: option.name, id: option.id }))

    const columns = [
        {
            field: 'work_center_name',
            headerName: t('work_center'),
            flex: 1.2,
            minWidth: 160,
            renderCell: (params) => (
                <div className='flex items-center gap-2'>
                    <i className="fa-solid fa-eye text-[#1976D2] text-[13px]"></i>
                    <span>{params.value}</span>
                </div>
            )
        },
        { field: 'hours_per_day', headerName: t('hours_per_day'), flex: 0.7, minWidth: 100 },
        { field: 'working_days', headerName: t('working_days'), flex: 0.7, minWidth: 100 },
        { field: 'available_hours', headerName: t('total_available_hours'), flex: 0.8, minWidth: 120, renderCell: (params) => formatHoursMinutes(params.value) },
        { field: 'required_hours', headerName: t('required_hours'), flex: 0.8, minWidth: 120, renderCell: (params) => formatHoursMinutes(params.value) },
        {
            field: 'remaining_hours',
            headerName: t('remaining_hours'),
            flex: 0.8,
            minWidth: 120,
            renderCell: (params) => (
                <span style={{ color: params.value < 0 ? '#C0362C' : '#171717' }}>{formatHoursMinutes(params.value)}</span>
            )
        },
        {
            field: 'utilization_percent',
            headerName: t('utilization'),
            flex: 1.1,
            minWidth: 160,
            sortable: false,
            renderCell: (params) => <UtilizationBar percent={params.value} status={params.row.status} />
        },
        {
            field: 'status',
            headerName: t('status'),
            flex: 0.9,
            minWidth: 140,
            renderCell: (params) => <CapacityStatusChip status={params.value} t={t} />
        }
    ]

    const dataGridSx = {
        '& .MuiDataGrid-columnHeaderTitle': { color: '#88909C !important', fontWeight: '400' },
        '& .MuiDataGrid-row': { cursor: 'pointer' },
        '& .MuiDataGrid-row:hover': { backgroundColor: '#FAFAFA' },
        '& .MuiDataGrid-cell': { borderBottom: '1px solid #E5E7EB' },
        border: 'none',
        background: 'white'
    }

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <AppLayout>
                <div className='p-6 flex flex-col w-full gap-5'>
                    {/* Planning Parameters */}
                    <div className='w-full bg-white rounded-md'>
                        <div className='p-5 flex justify-between items-center border-b border-[#E5E7EB]'>
                            <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('capacity_planning')}</p>
                        </div>

                        <div className='p-5'>
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 items-end'>
                                {/* Site */}
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('site')} *</p>
                                    <Autocomplete
                                        disablePortal
                                        disableClearable
                                        id="capacity-planning-site-autocomplete"
                                        options={optionsSites}
                                        value={siteId ? { label: siteName, id: siteId } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        onChange={handleSiteChange}
                                        sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                    />
                                </div>

                                {/* Period From */}
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('period_from')} *</p>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            sx={{ ...TEXTFIELD_STYLE, width: '100%' }}
                                            format="MM/DD/YYYY"
                                            value={periodFrom}
                                            onChange={(newValue) => {
                                                setPeriodFrom(newValue)
                                                if (newValue && periodTo && dayjs(periodTo).isBefore(dayjs(newValue), 'day')) {
                                                    setPeriodTo(null)
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </div>

                                {/* Period To */}
                                <div className='w-full flex items-end gap-4'>
                                    <div className='w-full'>
                                        <p className="text-[14px] text-[#718096]">{t('period_to')} *</p>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <MobileDatePicker
                                                sx={{ ...TEXTFIELD_STYLE, width: '100%' }}
                                                format="MM/DD/YYYY"
                                                value={periodTo}
                                                minDate={periodFrom || undefined}
                                                onChange={(newValue) => setPeriodTo(newValue)}
                                            />
                                        </LocalizationProvider>
                                    </div>

                                    <button
                                        type="button"
                                        disabled={!isFormValid()}
                                        onClick={runCapacityPlanning}
                                        className='flex-shrink-0 text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#1976D2] hover:bg-[#145CA3] disabled:bg-[#B8B7BC]'
                                    >
                                        {t('calculate')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {capacityResult ? (
                        <div className='w-full bg-white rounded-md'>
                            <div className='p-5 flex justify-between items-center border-b border-[#E5E7EB]'>
                                <div>
                                    <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('capacity_planning')} - {t('summary_view')}</p>
                                    <p className='text-[12px] text-[#88909C] pt-1'>
                                        {t('last_calculated')}: {lastRunAt ? lastRunAt.format('YYYY-MM-DD, h:mm:ss A') : '-'} &nbsp;•&nbsp; {t('site')}: {capacityResult.site?.name} &nbsp;•&nbsp; {t('period')}: {capacityResult.period_from} - {capacityResult.period_to}
                                    </p>
                                </div>
                                {/* <button
                                    type='button'
                                    onClick={() => Swal.fire({
                                        icon: 'info',
                                        text: t('export_not_implemented'),
                                        customClass: 'info',
                                        showCloseButton: true,
                                        iconColor: '#a8d5f1'
                                    })}
                                    className='flex items-center gap-1 flex-shrink-0'
                                    style={{ color: '#1976D2', fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '17.5px', letterSpacing: '0.4px' }}
                                >
                                    <i className="fa-solid fa-download"></i> {t('export')}
                                </button> */}
                            </div>
                            <div className='p-5 border-b border-[#E5E7EB] w-full'>
                                <div style={{ maxWidth: '320px' }}>
                                <p className="text-[14px] text-[#718096]">{t('work_center')}</p>
                                <Autocomplete
                                    disablePortal
                                    id="capacity-planning-work-center-filter"
                                    options={(capacityResult.work_centers ?? []).map((wc) => ({ id: wc.id, label: wc.work_center_name }))}
                                    value={workCenterFilter}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={(a, b) => setWorkCenterFilter(b)}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} placeholder={t('all')} />}
                                />
                                </div>
                            </div>
                            <div>
                                <DataGrid
                                    autoHeight
                                    headerHeight={64}
                                    disableColumnMenu
                                    disableSelectionOnClick
                                    rows={workCenterFilter ? (capacityResult.work_centers ?? []).filter((wc) => wc.id === workCenterFilter.id) : (capacityResult.work_centers ?? [])}
                                    columns={columns}
                                    sx={dataGridSx}
                                    pageSize={pageSize}
                                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                                    rowsPerPageOptions={[10, 25, 50]}
                                    onRowClick={(params) => openDetails(params.row)}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </AppLayout>

            {/* Work Center Capacity Details modal */}
            <Modal open={detailsOpen} onClose={closeDetails}>
                <Box sx={detailsModalStyle}>
                    <div className='flex justify-between items-center p-5 w-full' style={{ backgroundColor: '#336195', borderRadius: '5px 5px 0 0' }}>
                        <div className='flex items-baseline justify-between w-full'>
                            <p className='text-[18px] font-[600] text-white'>{selectedRow?.work_center_name} - {t('capacity_details')}</p>
                            <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-white">
                                <button onClick={closeDetails}><i className="fa-solid fa-plus"></i></button>
                            </div>
                        </div>
                    </div>

                    <div className='p-5'>
                        <div className='flex gap-5 pb-5 overflow-x-auto'>
                            <div className='border border-[#E5E7EB] rounded-md overflow-hidden h-fit divide-y divide-[#E5E7EB] flex-shrink-0' style={{ width: '310px' }}>
                                <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '13px', background: '#F7F9FB', color: '#667B96' }} className='uppercase px-2.5 pt-[15px] pb-[15px]'>{t('capacity')}</p>
                                <DetailField label={t('work_center')} value={`${selectedRow?.work_center_no} - ${selectedRow?.work_center_name}`} />
                                <DetailField label={t('hours_per_day')} value={selectedRow?.hours_per_day} />
                                <DetailField label={t('working_days')} value={selectedRow?.working_days} />
                                <DetailField label={t('total_available_hours')} value={formatHoursMinutes(selectedRow?.available_hours)} highlight />
                            </div>

                            <div className='border border-[#E5E7EB] rounded-md overflow-hidden h-fit divide-y divide-[#E5E7EB] flex-shrink-0' style={{ width: '310px' }}>
                                <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '13px', background: '#F7F9FB', color: '#667B96' }} className='uppercase px-2.5 pt-[15px] pb-[15px]'>{t('demand')}</p>
                                <DetailField label={t('required_hours')} value={formatHoursMinutes(selectedRow?.required_hours)} />
                                <DetailField label={t('remaining_hours')} value={formatHoursMinutes(selectedRow?.remaining_hours)} />
                                <DetailField label={t('utilization')} value={<UtilizationBar percent={selectedRow?.utilization_percent ?? 0} status={selectedRow?.status} />} />
                                <DetailField label={t('status')} value={<CapacityStatusChip status={selectedRow?.status} t={t} />} highlight />
                            </div>

                            <div className='border border-[#E5E7EB] rounded-md overflow-hidden h-fit divide-y divide-[#E5E7EB] flex-shrink-0' style={{ width: '310px' }}>
                                <p style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '13px', background: '#F7F9FB', color: '#667B96' }} className='uppercase px-2.5 pt-[15px] pb-[15px]'>{t('demand_breakdown')}</p>
                                <DetailField label={t('open_production_orders')} value={formatHoursMinutes(selectedRow?.demand_breakdown?.open_production_orders_hours)} />
                                <DetailField label={t('planned_production_orders')} value={formatHoursMinutes(selectedRow?.demand_breakdown?.planned_production_orders_hours)} />
                                <DetailField label={t('setup_hours')} value={formatHoursMinutes(selectedRow?.demand_breakdown?.setup_hours)} />
                                <DetailField label={t('total_required_hours')} value={formatHoursMinutes(selectedRow?.required_hours)} highlight />
                            </div>
                        </div>

                        <p className='text-[13px] font-[600] uppercase text-[#A1ACB8] pb-2'>{t('scheduled_orders')}</p>
                        <div className='border border-[#E5E7EB] rounded-md overflow-hidden'>
                            <div className='grid grid-cols-6 gap-2 px-4 py-2 text-[12px] font-[600] text-[#88909C] border-b border-[#E5E7EB] bg-[#FAFAFA]'>
                                <span>{t('po_number')}</span>
                                <span>{t('item')}</span>
                                <span>{t('operation')}</span>
                                <span>{t('required_hours')}</span>
                                <span>{t('due_date')}</span>
                                <span>{t('status')}</span>
                            </div>
                            {(selectedRow?.scheduled_orders ?? []).map((order, idx) => (
                                <div key={idx} className={`grid grid-cols-6 gap-2 px-4 py-2.5 text-[13px] ${idx > 0 ? 'border-t border-[#E5E7EB]' : ''}`}>
                                    {order.order_id ? (
                                        <span
                                            className='font-[500]'
                                            style={{ color: '#1976D2', cursor: 'pointer' }}
                                            onClick={() => window.open(`/production-order/${order.order_id}`, '_blank')}
                                        >
                                            {order.order_no}
                                        </span>
                                    ) : (
                                        <span className='font-[500]'>{order.order_no}</span>
                                    )}
                                    <span>{order.item_no} - {order.description}</span>
                                    <span>{order.operation}</span>
                                    <span>{formatHoursMinutes(order.required_hours)}</span>
                                    <span>{order.due_date}</span>
                                    <span>
                                        <Chip label={order.status} size='small' sx={{ backgroundColor: '#EAF2FB', color: '#336195', fontWeight: 600 }} />
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Box>
            </Modal>
        </>
    )
}

export default CapacityPlanning
